import React, { useEffect, useRef, useState } from 'react';
import styles from '../Patient.module.css';
import { useSelector, useDispatch } from 'react-redux';
import PatientDetailDropdownCard from '../../../components/PatientDetailDropdownCard';
import FilterPatientView from '../FilterPatientDetail';
import ReportsDetails from '../CardDetails/ReportsDetails';
import useAxios from '../../../services/AxiosConfig';
import { setFormField } from '../../../slicers/PatientSlice';
import { formatDateWithFullMonth, formatDateWithFullMonthAndTime } from '../../../utils/dateUtils';
import { useWebSocket } from '../../../services/webSocket/useWebSocket';

const ReportsView = () => {
  const dispatch = useDispatch();
  const axiosConfig = useSelector((state) => state.axiosConfig);
  const axios = useAxios(axiosConfig.envURL);
  const userAccountId = useSelector((state) => state.userInfo.id);
  const dateFormat = useSelector((state) => state.userInfo.dateFormat);
  const { id, reports, taskNonCriticalReportId } = useSelector((state) => state.patient);
  const { searchText, dateOrder, selectedReportTypes } = useSelector((state) => state.patientDetailFilter);

  // Reference for each report to scroll to
  const reportRefs = useRef([]);
  const [openedReportId, setOpenedReportId] = useState(null);
  const { receivedMessage } = useWebSocket();

  const sortedReports = reports
    .filter((item) => {
      const matchesReportType = 
      selectedReportTypes.length === 0 || // Return all if no filters are selected
      selectedReportTypes.some(type => type.label === item.status); // Match `item.status` with `type.label`

    // Check if the search text matches the description
    const matchesSearchText = item.description.toLowerCase().includes(searchText.toLowerCase());

    // Return items that match both the search text and the report type
    return matchesSearchText && matchesReportType;
    })
    .sort((a, b) => {
      const startDateA = new Date(a.createdDate);
      const startDateB = new Date(b.createdDate);
      return dateOrder === "ascending" ? startDateA - startDateB : startDateB - startDateA;
    });

  useEffect(() => {
    if (taskNonCriticalReportId && reportRefs.current) {
      const reportIndex = sortedReports.findIndex(report => report.nonCriticalReportId === taskNonCriticalReportId);
      if (reportIndex !== -1) {
        const report = sortedReports[reportIndex];
        const element = reportRefs.current[reportIndex];
        if (element) {
          element.scrollIntoView({ behavior: 'smooth'});
          setOpenedReportId(taskNonCriticalReportId);
          handleFirstOpen(report)();
        }
      }
    }
  }, [taskNonCriticalReportId, reports]);


  const getResponses = async (data, currentReports = []) => {
    try {
      // Clear responses for the reports we're about to fetch so we don't show stale data
      const idsToRefresh = new Set(data.map((r) => r.nonCriticalReportId));
      if (idsToRefresh.size > 0 && currentReports.length > 0) {
        const reportsWithClearedResponses = currentReports.map((r) =>
          idsToRefresh.has(r.nonCriticalReportId) ? { ...r, responses: [] } : r
        );
        dispatch(setFormField({ key: "reports", value: reportsWithClearedResponses }));
      }

      const responsesPromises = data.map(async (report) => {
        try {
          const response = await axios.get(`report/responses/${userAccountId}/${report.nonCriticalReportId}?showInternal=true`);
          return Array.isArray(response.data.data) ? response.data.data : [];
        } catch (err) {
          console.log(err);
          return [];
        }
      });

      const responses = await Promise.all(responsesPromises);
      data.forEach((report, index) => {
        report["responses"] = responses[index];
      });
      return data;
    } catch (err) {
      console.error('Error fetching responses:', err);
      return data;
    }
  };

  const fetchReports = async () => {
    try {
      dispatch(setFormField({ key: "reports", value: [] }));
      const reportResponse = await axios.get(`report/${id}?physicianId=${userAccountId}&reportType=healthConcern`);
      if (Array.isArray(reportResponse.data.data)) {
        const dataWithResponses = await getResponses(reportResponse.data.data, []);
        dispatch(setFormField({ key: "reports", value: dataWithResponses }));
      } else {
        console.error('Error fetching patient info');
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  useEffect(() => {
    if ((receivedMessage?.type === 'report_response' && receivedMessage?.patientId === id) || (receivedMessage?.type === 'new_patient_submitted_report' && receivedMessage?.patientId === id) ) {
      fetchReports();
    }
  }, [receivedMessage]);

  const handleFirstOpen = (report) => async () => {
    if (report.Unread > 0) {
      try {
        await axios.post(`report/markResponsesAsRead`, {
          nonCriticalReportId: report.nonCriticalReportId,
          accountId: userAccountId
        });
        const updatedReport = { ...report, Unread: 0 };
        const updatedReports = reports.map((r) =>
          r.nonCriticalReportId === report.nonCriticalReportId ? updatedReport : r
        );
        dispatch(setFormField({ key: "reports", value: updatedReports }));
      } catch (err) {
        console.error('Failed to mark report as read:', err);
      }
    }
  };

  return (
    <div className={styles.ViewContainer}>
      <FilterPatientView isSearch={true} isDate={true} isReport={true} />
      <div className={styles.PatientViewScrollContainer}>
        {sortedReports.map((report, index) => {
          const labels = {
            unread: { name: report.Unread, color: "#9F1919" },
            open: { name: "Open", color: "#634D02" },
            new: { name: "New", color: "#1F5C29" },
            resolved: { name: "Resolved", color: "#686869" }
          };
          const label = report.status === "Resolved"
            ? labels.resolved
            : report.Unread !== 0
              ? labels.unread
              : report.responses.length !== 0
                ? labels.open
                : labels.new;

          const formattedDate = formatDateWithFullMonth(report.createdDate, dateFormat);
          const formattedResolvedDate = report.resolvedDate ? `Resolved: ${formatDateWithFullMonthAndTime(report.resolvedDate, dateFormat)}` : null;


          return (
            <div key={index} ref={el => reportRefs.current[index] = el}>
              <PatientDetailDropdownCard
                title={report.title || "Non Critical Report"}
                subtitle2={formattedDate}
                subtitle3={formattedResolvedDate}
                label={label}
                cardHeight="auto"
                cardInfo={<ReportsDetails data={report} />}
                onFirstOpen={handleFirstOpen(report)}
                isOpen={openedReportId === report.nonCriticalReportId}
                headerBackground={openedReportId === report.nonCriticalReportId ? "rgba(203, 243, 210, 0.40)" : "white"}
              />
            </div>
          );
        })}
        {sortedReports.length === 0 &&
          <div className={styles.emptyView}>
            Patient has not submitted any reports
          </div>
        }
      </div>
    </div>
  );
};

export default ReportsView;
